/**
 * Statistics filters and number formatting. The period itself is computed by
 * the server (in Israel local days); the portal only says which one it wants.
 */

export const PRESETS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'custom', label: 'Custom range' },
] as const;

export interface StatisticsFilters {
  preset: string;
  from: string;
  to: string;
  campus: string;
}

export const DEFAULT_FILTERS: StatisticsFilters = { preset: '30d', from: '', to: '', campus: '' };

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Filters from a URL query string, falling back to the defaults for anything invalid. */
export function filtersFromSearch(search: URLSearchParams): StatisticsFilters {
  const from = search.get('from') || '';
  const to = search.get('to') || '';
  const preset = search.get('preset') || '';
  const custom = YMD.test(from) && YMD.test(to);
  return {
    preset: custom ? 'custom' : PRESETS.some((p) => p.value === preset && p.value !== 'custom') ? preset : '30d',
    from: custom ? from : '',
    to: custom ? to : '',
    campus: search.get('campus') || '',
  };
}

/** The query parameters the statistics API takes for these filters. */
export function filtersToParams(filters: StatisticsFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.preset === 'custom' && filters.from && filters.to) {
    params.from = filters.from;
    params.to = filters.to;
  } else {
    params.preset = filters.preset === 'custom' ? '30d' : filters.preset;
  }
  if (filters.campus) params.campus = filters.campus;
  return params;
}

/** Same filters as a query string, so links between screens keep them. */
export function filtersToSearch(filters: StatisticsFilters): string {
  const params = filtersToParams(filters);
  if (filters.preset === 'custom') params.preset = 'custom';
  return new URLSearchParams(params).toString();
}

const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : integer.format(value);

export const formatDecimal = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : decimal.format(value);

export const formatShare = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${decimal.format(value * 100)}%`;

/** Minutes as "12 min", "3.5 h" or "2 days". */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 90) return `${decimal.format(minutes)} min`;
  if (minutes < 48 * 60) return `${decimal.format(minutes / 60)} h`;
  return `${decimal.format(minutes / 1440)} days`;
}

export const formatHours = (hours: number | null | undefined): string =>
  hours === null || hours === undefined ? '—' : formatMinutes(hours * 60);

export const formatDays = (days: number | null | undefined): string =>
  days === null || days === undefined ? '—' : formatMinutes(days * 1440);

export function formatDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jerusalem',
  });
}

export interface Delta {
  /** Change against the previous period, as a fraction (0.25 = +25%), or null. */
  change: number | null;
  direction: 'up' | 'down' | 'flat' | 'none';
}

/** How a figure moved against the previous period. */
export function delta(current: number | null, previous: number | null): Delta {
  if (current === null || previous === null) return { change: null, direction: 'none' };
  if (current === previous) return { change: 0, direction: 'flat' };
  if (previous === 0) return { change: null, direction: current > 0 ? 'up' : 'down' };
  const change = (current - previous) / Math.abs(previous);
  return { change, direction: change > 0 ? 'up' : 'down' };
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WINDOWS = [
  { key: 'morning', label: 'Morning (06–12)' },
  { key: 'noon', label: 'Afternoon (12–18)' },
  { key: 'evening', label: 'Evening (18–24)' },
  { key: 'night', label: 'Night (00–06)' },
];
